import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import './AIAssistantPopup.css';
import ArchieAvatar from '../../felix_icon.jpg';

// cap stored messages to avoid filling localStorage
const HISTORY_KEY = 'ai-chat-history';
const SIZE_KEY = 'ai-popup-size';
const MAX_STORED = 50;

function saveHistory(messages) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-MAX_STORED)));
  } catch (err) {
    console.error('Failed to save chat history:', err);
  }
}

function loadHistory() {
  try {
    const saved = localStorage.getItem(HISTORY_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed?.length > 0) return parsed;
    }
  } catch (err) {
    console.error('Failed to load chat history:', err);
  }
  return null;
}

const INITIAL_MESSAGE = {
  role: 'assistant',
  content: "👋 Hi! I'm Archie, your ArQive AI assistant. Ask me anything about LGBTQ+ history!"
};

const StorySuggestionCard = ({ msg, onSuggestStory, onNavigate }) => {
  const [confirmed, setConfirmed] = React.useState(false);
  const story = msg.storyData;
  const hasSimilar = msg.similar_stories?.length > 0;
  const hasLocation = story.latitude && story.longitude;

  // backend already deduplicates, no need to do it here
  const similarStories = msg.similar_stories || [];

  const handleAdd = () => {
    if (hasSimilar && !confirmed) {
      setConfirmed(true);
      return;
    }
    if (onSuggestStory) onSuggestStory(story);
  };

  const handleLocate = (s) => {
    if (onNavigate) onNavigate({ latitude: s.latitude, longitude: s.longitude, pinId: s.id, title: s.title });
  };

  const matchLabel = (type) => {
    if (type === 'exact') return { text: 'exact match', color: '#059669', bg: '#d1fae5' };
    if (type === 'partial') return { text: 'partial match', color: '#d97706', bg: '#fef3c7' };
    return { text: 'nearby', color: '#6366f1', bg: '#ede9fe' };
  };

  return (
    <div style={{
      background: '#f0ebff', border: '1px solid #c4b5fd',
      borderRadius: '10px', padding: '12px', marginTop: '10px', fontSize: '13px',
    }}>
      <div style={{ fontWeight: 600, marginBottom: '4px', color: '#4c1d95' }}>{story.title}</div>
      {(story.address || story.locality) && (
        <div style={{ color: '#666', marginBottom: '4px' }}>
          📍 {(story.address && story.address.toLowerCase() !== 'unknown' && story.address !== 'null')
            ? story.address
            : [story.locality, story.region].filter(Boolean).join(', ')}
        </div>
      )}
      {story.startDate && <div style={{ color: '#666', marginBottom: '4px' }}>📅 {story.startDate}</div>}
      {story.description && (
        <div style={{ color: '#444', marginBottom: '8px', lineHeight: 1.4 }}>
          {story.description.slice(0, 150)}...
        </div>
      )}

      {hasSimilar && (
        <div style={{
          background: '#fff7ed', border: '1px solid #fed7aa',
          borderRadius: '8px', padding: '10px', marginBottom: '10px',
        }}>
          <div style={{ fontWeight: 600, color: '#c2410c', marginBottom: '8px', fontSize: '12px' }}>
            ⚠️ Similar stories already exist on the map:
          </div>
          {similarStories.map((s, i) => {
            const label = matchLabel(s.match_type);
            return (
              <div key={i} style={{
                background: 'white', border: '1px solid #e5e7eb',
                borderRadius: '6px', padding: '8px 10px', marginBottom: '6px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '2px' }}>
                    "{s.title}"
                    <span style={{
                      marginLeft: '6px', fontSize: '10px',
                      background: label.bg, color: label.color,
                      padding: '1px 5px', borderRadius: '4px',
                    }}>{label.text}</span>
                  </div>
                  {s.address && <div style={{ fontSize: '13px', color: '#6b7280' }}>📍 {s.address}</div>}
                </div>
                {s.latitude && s.longitude && (
                  <button onClick={() => handleLocate(s)} title="Locate on map"
                    style={{
                      background: '#667eea', border: 'none', borderRadius: '6px',
                      padding: '5px 10px', fontSize: '12px', cursor: 'pointer',
                      color: 'white', flexShrink: 0, whiteSpace: 'nowrap',
                    }}>
                    🗺️ Locate
                  </button>
                )}
              </div>
            );
          })}
          <div style={{ color: '#9a3412', fontSize: '11px', marginTop: '4px' }}>
            Click "Locate" to view an existing story, or add this as a new one below.
          </div>
        </div>
      )}

      {hasLocation && !hasSimilar && (
        <button
          onClick={() => { if (onNavigate) onNavigate({ latitude: story.latitude, longitude: story.longitude, title: story.title }); }}
          style={{
            background: 'white', color: '#667eea', border: '1.5px solid #667eea',
            borderRadius: '8px', padding: '7px 16px', fontSize: '13px',
            cursor: 'pointer', width: '100%', marginBottom: '6px',
          }}>
          🗺️ Show this location on the map
        </button>
      )}

      <button onClick={handleAdd} style={{
        background: hasSimilar && !confirmed
          ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white', border: 'none', borderRadius: '8px',
        padding: '8px 16px', fontSize: '13px', cursor: 'pointer',
        width: '100%', marginTop: '2px',
      }}>
        {hasSimilar && !confirmed ? '⚠️ Similar story exists — click to confirm add anyway' : '➕ Add this story to the map'}
      </button>

      {msg.sources?.length > 0 && (
        <div style={{ marginTop: '8px', fontSize: '11px', color: '#888' }}>
          Sources: {msg.sources.slice(0, 2).map((s, i) => (
            <a key={i} href={s} target="_blank" rel="noopener noreferrer"
              style={{ color: '#667eea', marginRight: '8px' }}>[{i + 1}]</a>
          ))}
        </div>
      )}
    </div>
  );
};

const AIAssistantPopup = ({ onClose, onSuggestStory, onNavigate }) => {
  const [messages, setMessages] = useState(() => loadHistory() || [INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);

  // delay typing indicator to avoid flash on fast responses
  const typingTimerRef = useRef(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);
  const messagesRef = useRef(messages);

  const [position, setPosition] = useState({ x: 20, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // save with cap on every change
  useEffect(() => { saveHistory(messages); }, [messages]);

  useEffect(() => {
    return () => {
      saveHistory(messagesRef.current);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // restore saved size on mount, then observe future size changes
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const saved = localStorage.getItem(SIZE_KEY);
    if (saved) {
      try {
        const { width, height } = JSON.parse(saved);
        el.style.width = width;
        el.style.height = height;
      } catch (_) {}
    }
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      localStorage.setItem(SIZE_KEY, JSON.stringify({
        width: width + 'px',
        height: height + 'px',
      }));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return;
    // don't drag if clicking near resize handle (bottom-right corner)
    const rect = containerRef.current.getBoundingClientRect();
    if (e.clientX > rect.right - 20 || e.clientY > rect.bottom - 20) return;
    setIsDragging(true);
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !containerRef.current) return;
      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;
      const maxX = window.innerWidth - containerRef.current.offsetWidth;
      const maxY = window.innerHeight - containerRef.current.offsetHeight;
      setPosition({ x: Math.max(0, Math.min(newX, maxX)), y: Math.max(0, Math.min(newY, maxY)) });
    };
    const handleMouseUp = () => setIsDragging(false);
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const sendImageMessage = async (imageData, userText) => {
    setMessages(prev => [...prev, {
      role: 'user',
      content: userText || 'What can you tell me about this image?',
      imagePreview: imageData.previewUrl,
    }]);
    setInput('');
    setPendingImage(null);
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_ARQIVE}/ai/chat-image/`,
        { message: userText || '', image: imageData.base64, media_type: imageData.mediaType },
        { headers: { 'Content-Type': 'application/json', 'X-Arqive-Api-Key': process.env.REACT_APP_API_KEY } }
      );
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.success ? response.data.reply : '😔 Sorry, could not analyze the image.'
      }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: '😔 Error analyzing image. Please try again.' }]);
    }
    setLoading(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPendingImage({ base64: ev.target.result.split(',')[1], mediaType: file.type, previewUrl: ev.target.result });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
          setPendingImage({ base64: ev.target.result.split(',')[1], mediaType: file.type, previewUrl: ev.target.result });
        };
        reader.readAsDataURL(file);
        return;
      }
    }
  };

  // cache coords after first successful fetch
  const cachedCoordsRef = useRef(null);

  const getNearMeCoords = () => new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    // return cached coords immediately if available
    if (cachedCoordsRef.current) { resolve(cachedCoordsRef.current); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        cachedCoordsRef.current = coords;
        resolve(coords);
      },
      () => resolve(null),
      { timeout: 10000, maximumAge: 60000 }
    );
  });

  // pre-fetch location on mount so it's ready when user asks
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          cachedCoordsRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        },
        () => {},
        { timeout: 10000, maximumAge: 60000 }
      );
    }
  }, []);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (pendingImage) {
      await sendImageMessage(pendingImage, input.trim());
      return;
    }

    if (!input.trim()) return;

    const userInput = input;
    setMessages(prev => [...prev, { role: 'user', content: userInput }]);
    setInput('');
    setLoading(true);

    // detect "near me" and attach coordinates
    let userCoords = null;
    if (/near me|nearby|close to me|around me/i.test(userInput)) {
      console.log('Requesting geolocation...');
      userCoords = await getNearMeCoords();
      console.log('Geolocation result:', userCoords);
    }



    try {
      const recentHistory = messagesRef.current
        .slice(-6)
        .filter(m => (m.role === 'user' || m.role === 'assistant') && !m.isTemp)
        .map(m => ({ role: m.role, content: m.content || '' }));

      const response = await axios.post(
        `${process.env.REACT_APP_ARQIVE}/ai/chat/`,
        { message: userInput, history: recentHistory, user_lat: userCoords?.lat, user_lng: userCoords?.lng },
        {
          headers: { 'Content-Type': 'application/json', 'X-Arqive-Api-Key': process.env.REACT_APP_API_KEY },
          validateStatus: (status) => status < 500, // don't throw on 4xx
        }
      );

      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      setMessages(prev => prev.filter(m => !m.isTemp));

      if (response.data.success) {
        if (response.data.type === 'story_suggestion' && response.data.story) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: response.data.message,
            storyData: response.data.story,
            sources: response.data.sources || [],
            similar_stories: response.data.similar_stories || [],
            type: 'story_suggestion',
          }]);
        } else if (response.data.type === 'search_results') {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: response.data.reply,
            pins: response.data.pins || [],
            type: 'search_results',
            offer_web_search: response.data.offer_web_search || false,
            web_search_topic: response.data.web_search_topic || '',
          }]);
        } else {
          setMessages(prev => [...prev, { role: 'assistant', content: response.data.reply }]);
        }
      } else {
        // show backend error message directly instead of generic error
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '😔 ' + (response.data.error || 'Sorry, something went wrong. Please try again.')
        }]);
      }
    } catch {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      setMessages(prev => prev.filter(m => !m.isTemp));
      setMessages(prev => [...prev, { role: 'assistant', content: '😔 Sorry, I encountered an error. Please try again in a moment.' }]);
    }
    setLoading(false);
  };

  const clearHistory = () => {
    setMessages([INITIAL_MESSAGE]);
    saveHistory([INITIAL_MESSAGE]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  const positionStyle = position.y !== null
    ? { left: position.x, top: position.y }
    : { left: 20, bottom: 100 };

  const renderMessage = (msg, idx) => {
    if (msg.type === 'story_suggestion' && msg.storyData) {
      return (
        <div key={idx} className="message assistant">
          <div className="message-bubble">
            <ReactMarkdown>{msg.content}</ReactMarkdown>
            <StorySuggestionCard msg={msg} onSuggestStory={onSuggestStory} onNavigate={onNavigate} />
          </div>
        </div>
      );
    }

    if (msg.type === 'search_results' && msg.offer_web_search && (!msg.pins || msg.pins.length === 0)) {
      // no results at all — show only web search button
      return (
        <div key={idx} className="message assistant">
          <div className="message-bubble">
            <ReactMarkdown>{msg.content}</ReactMarkdown>
            <button
              onClick={() => {
                const topic = msg.web_search_topic
                  .replace(/^(find|search|look for|show me|find stories?|search stories?|search the web for)\s+(the\s+)?(story|stories)?\s*(about|for|on|at|in)?\s*/i, '')
                  .trim();
                setInput(`Search the story about ${topic}`);
              }}
              style={{
                marginTop: '10px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white', border: 'none', borderRadius: '8px',
                padding: '8px 16px', fontSize: '14px', cursor: 'pointer', width: '100%',
              }}
            >
              {`🌐 Search the web for "${msg.web_search_topic.replace(/^(find|search|look for|show me|find stories?|search stories?|search the web for)\s+(the\s+)?(story|stories)?\s*(about|for|on|at|in)?\s*/i, '').trim()}"`}
            </button>
          </div>
        </div>
      );
    }

    if (msg.type === 'search_results' && msg.pins?.length > 0) {
      return (
        <div key={idx} className="message assistant">
          <div className="message-bubble">
            <ReactMarkdown>{msg.content}</ReactMarkdown>
            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {msg.pins.map((pin, i) => (
                <div key={i} style={{
                  background: '#f0ebff', border: '1px solid #c4b5fd',
                  borderRadius: '8px', padding: '8px 10px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: '#4c1d95' }}>
                      {pin.title}
                      {pin.match_score !== undefined && pin.match_score !== null && (
                        <span style={{ fontSize: '12px', color: '#888', fontWeight: 400, marginLeft: '6px' }}>
                          {pin.match_score}% match
                        </span>
                      )}
                    </div>
                    {pin.address && <div style={{ fontSize: '13px', color: '#666' }}>📍 {pin.address}</div>}
                    {pin.startDate && <div style={{ fontSize: '13px', color: '#666' }}>📅 {pin.startDate}</div>}
                    {pin.distance_miles !== undefined && pin.distance_miles !== null && (
                      <div style={{ fontSize: '12px', color: '#999' }}>📏 {pin.distance_miles} miles away</div>
                    )}
                  </div>
                  {pin.latitude && pin.longitude && (
                    <button
                      onClick={() => { if (onNavigate) onNavigate({ latitude: pin.latitude, longitude: pin.longitude, pinId: pin.id, title: pin.title }); }}
                      style={{
                        background: '#667eea', color: 'white', border: 'none',
                        borderRadius: '6px', padding: '5px 10px', fontSize: '12px',
                        cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
                      }}
                    >
                      🗺️ Locate
                    </button>
                  )}
                </div>
              ))}
            </div>
            {msg.offer_web_search && msg.web_search_topic && (
              <button
                onClick={() => {
                  const topic = msg.web_search_topic
                    .replace(/^(find|search|look for|show me|find stories?|search stories?|search the web for)\s+(the\s+)?(story|stories)?\s*(about|for|on|at|in)?\s*/i, '')
                    .trim();
                  setInput(`Search the story about ${topic}`);
                }}
                style={{
                  marginTop: '8px',
                  background: 'rgba(102,126,234,0.15)', color: '#667eea',
                  border: '1px solid #667eea', borderRadius: '8px',
                  padding: '6px 14px', fontSize: '13px', cursor: 'pointer', width: '100%',
                }}
              >
                {`🌐 Search the web for "${msg.web_search_topic.replace(/^(find|search|look for|show me|find stories?|search stories?|search the web for)\s+(the\s+)?(story|stories)?\s*(about|for|on|at|in)?\s*/i, '').trim()}"`}
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={idx} className={`message ${msg.role}`}>
        <div className="message-bubble">
          {msg.imagePreview && (
            <img src={msg.imagePreview} alt="uploaded"
              style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '6px', display: 'block' }} />
          )}
          {msg.role === 'assistant'
            ? <ReactMarkdown>{msg.content}</ReactMarkdown>
            : msg.content
          }
        </div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="ai-popup-container"
      style={{ position: 'fixed', ...positionStyle, cursor: isDragging ? 'grabbing' : 'default', zIndex: 2000 }}
    >
      <div className="ai-popup-header" onMouseDown={handleMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={ArchieAvatar} alt="Archie"
            style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover', objectPosition: 'center top', flexShrink: 0 }} />
          <h3 style={{ margin: 0 }}>Archie</h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button onClick={clearHistory} className="ai-popup-text-btn" title="Clear chat history">clear</button>
          <button className="ai-popup-text-btn" onClick={onClose}>close</button>
        </div>
      </div>

      <div className="ai-popup-messages">
        {messages.map((msg, idx) => renderMessage(msg, idx))}
        {loading && (
          <div className="message assistant">
            <div className="message-bubble">
              <div className="typing-indicator"><span></span><span></span><span></span></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={sendMessage} className="ai-popup-input-form" style={{ alignItems: 'flex-end', padding: '8px', gap: '6px' }}>
        <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageUpload} style={{ display: 'none' }} />

        <button
          type="button"
          onClick={() => { if (fileInputRef.current) fileInputRef.current.click(); }}
          disabled={loading}
          title="Upload an image"
          style={{
            background: 'none', border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '18px', padding: '0 4px',
            opacity: loading ? 0.4 : 1, flexShrink: 0, marginBottom: '4px',
          }}
        >📎</button>

        
        <div style={{
          flex: 1,
          border: '1.5px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '16px',
          padding: '8px 12px',
          background: 'rgba(255, 255, 255, 0.18)',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          minHeight: '36px',
        }}>
          {pendingImage && (
            <div style={{ position: 'relative', display: 'inline-block', alignSelf: 'flex-start' }}>
              <img
                src={pendingImage.previewUrl} alt="pending"
                style={{ maxHeight: '100px', maxWidth: '200px', borderRadius: '8px', display: 'block', objectFit: 'cover' }}
              />
              <button
                type="button"
                onClick={() => setPendingImage(null)}
                style={{
                  position: 'absolute', top: '3px', right: '3px',
                  background: 'rgba(0,0,0,0.6)', color: 'white',
                  border: 'none', borderRadius: '50%',
                  width: '18px', height: '18px',
                  cursor: 'pointer', fontSize: '11px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
          )}

          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !input && pendingImage) setPendingImage(null);
            }}
            placeholder={pendingImage ? "Add a message (optional)..." : "Ask Archie anything... (Ctrl+V to paste image)"}
            disabled={loading}
            style={{
              border: 'none', outline: 'none', background: 'transparent',
              fontSize: '14px', width: '100%', height: '28px', padding: 0, color: '#fff',
            }}
          />
        </div>

        <button
          type="submit"
          disabled={loading || (!input.trim() && !pendingImage)}
          className="ai-popup-send-btn"
          style={{ marginBottom: '4px', flexShrink: 0 }}
        >
          {loading ? '...' : 'send'}
        </button>
      </form>
    </div>
  );
};

export default AIAssistantPopup;